import React from 'react';
import Parser from 'html-react-parser';
import './StrapiBlocksRenderer.css';

const StrapiBlocksRenderer = ({ blocks }) => {
  if (!blocks || !Array.isArray(blocks)) {
    return null;
  }

  // Helper function to detect if text contains HTML tags
  const containsHTML = (text) => {
    if (!text) return false;
    return /<\/?[a-z][\s\S]*>/i.test(text);
  };

  // Helper function to check if text is table-related HTML
  const isTableHTML = (text) => {
    if (!text) return false;
    return /<\/?(?:table|thead|tbody|tfoot|tr|th|td|caption|col|colgroup)[\s>]/i.test(text);
  };

  const renderChildren = (children) => {
    if (!children) return null;

    return children.map((child, index) => {
      if (child.type === 'text') {
        let text = child.text;

        // Check if text contains HTML tags (like <table>, <div>, etc.)
        if (containsHTML(text)) {
          // Parse and render HTML content
          return <React.Fragment key={index}>{Parser(text)}</React.Fragment>;
        }

        // Apply text formatting for non-HTML text
        if (child.bold) {
          text = <strong key={index}>{text}</strong>;
        }
        if (child.italic) {
          text = <em key={index}>{text}</em>;
        }
        if (child.underline) {
          text = <u key={index}>{text}</u>;
        }
        if (child.strikethrough) {
          text = <s key={index}>{text}</s>;
        }
        if (child.code) {
          text = <code key={index}>{text}</code>;
        }

        return <React.Fragment key={index}>{text}</React.Fragment>;
      }

      if (child.type === 'link') {
        return (
          <a
            key={index}
            href={child.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {renderChildren(child.children)}
          </a>
        );
      }

      return renderBlock(child, index);
    });
  };

  const renderBlock = (block, index) => {
    switch (block.type) {
      case 'paragraph':
        // Check if paragraph contains HTML content (like tables)
        const hasHTML = block.children?.some(child =>
          child.type === 'text' && containsHTML(child.text)
        );

        // If it contains HTML (tables, divs, etc.), render without any wrapper
        // This allows table elements to be properly nested
        if (hasHTML) {
          return (
            <React.Fragment key={index}>
              {renderChildren(block.children)}
            </React.Fragment>
          );
        }

        // Normal paragraph
        return (
          <p key={index}>
            {renderChildren(block.children)}
          </p>
        );

      case 'heading':
        const HeadingTag = `h${block.level}`;
        return (
          <HeadingTag key={index}>
            {renderChildren(block.children)}
          </HeadingTag>
        );

      case 'list':
        const ListTag = block.format === 'ordered' ? 'ol' : 'ul';
        return (
          <ListTag key={index}>
            {renderChildren(block.children)}
          </ListTag>
        );

      case 'list-item':
        return (
          <li key={index}>
            {renderChildren(block.children)}
          </li>
        );

      case 'quote':
        return (
          <blockquote key={index}>
            {renderChildren(block.children)}
          </blockquote>
        );

      case 'code':
        return (
          <pre key={index}>
            <code>{renderChildren(block.children)}</code>
          </pre>
        );

      case 'image':
        return (
          <img
            key={index}
            src={block.image.url}
            alt={block.image.alternativeText || ''}
            width={block.image.width}
            height={block.image.height}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="skills-item-content">
      {blocks.map((block, index) => renderBlock(block, index))}
    </div>
  );
};

export default StrapiBlocksRenderer;